import React, { useRef } from 'react';
import { Formik } from 'formik';
import { Form } from 'formik-antd';
import { Button, Modal, Row, Col, Divider } from 'antd';
import * as Yup from 'yup';

import MailTheChecks, {
  MailTheChecksFormValues,
  isMailTheChecksTypeSelected,
  mailTheChecksValidationSchema,
} from './MailTheChecks';
import DirectDeposit, {
  DirectDepositFormValues,
  isDirectDepositTypeSelected,
  directDepositValidationSchema,
} from './DirectDeposit';
import International, {
  isInternationalTypeSelected,
  internationalValidationSchema,
  InternationalFormValues,
} from './International';
import {
  BOUsersBankingInfoTypeEnum,
  BankingInformationRequestModel,
  BankingInformationResponseModel,
  EntityInformationResponseModel,
  ResourceDownloadResponse,
} from 'api/backofficeApiClient';
import Radio from 'components/formik/Radio';
import BaseAlert from 'components/wrappers/BaseAlert';
import FormItem from 'components/formik/FormItem';
import SubmitButton from 'components/formik/SubmitButton';
import { ApiErrorMessage } from 'constants/ErrorMessages';
import { saveBankingInformation } from 'redux/reducers/accountSlice';
import { BaseModal, BaseModalFooter } from 'components/wrappers/BaseModal';
import { isAdminSelector } from 'redux/selectors/authenticationSelectors';
import BankingInformationUploader from 'components/modals/BankingInformationUploader';
import { saveRecruiterBankingInformation } from 'redux/reducers/recruiterSlice';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { useAppSelector } from 'hooks/useAppSelector';

type Props = {
  companyId?: string;
  visible: boolean;
  onClose: () => void;
  onEdit: () => Promise<void>;
  bankingInfo?: BankingInformationResponseModel | null;
  entityInfo?: EntityInformationResponseModel | null;
  bankingFile?: ResourceDownloadResponse;
};

export interface BankingInfoFormValues {
  bankingInfoType: BOUsersBankingInfoTypeEnum;
  directDepositDetail: DirectDepositFormValues;
  mailMyChecksDetail: MailTheChecksFormValues;
  internationalDetail: InternationalFormValues;
  bankingFile: string[];
}

const validationSchema = Yup.object({
  bankingInfoType: Yup.number(),
  bankingFile: Yup.array()
    .of(Yup.string())
    .required('Field is required')
    .min(1, 'Field is required'),
  directDepositDetail: Yup.object().when('bankingInfoType', {
    is: isDirectDepositTypeSelected,
    then: directDepositValidationSchema,
  }),
  mailMyChecksDetail: Yup.object().when('bankingInfoType', {
    is: isMailTheChecksTypeSelected,
    then: mailTheChecksValidationSchema,
  }),
  internationalDetail: Yup.object().when('bankingInfoType', {
    is: isInternationalTypeSelected,
    then: internationalValidationSchema,
  }),
});

export default function EditBankingInfoModal(props: Props) {
  const {
    entityInfo,
    bankingInfo,
    visible,
    onClose,
    onEdit,
    companyId,
    bankingFile,
  } = props;
  const dispatch = useAppDispatch();
  const isAdmin = useAppSelector(isAdminSelector);
  const formRef: any = useRef();

  const initialValues: BankingInfoFormValues = {
    bankingInfoType:
      bankingInfo?.bankingInformationType ||
      BOUsersBankingInfoTypeEnum.DirectDeposit,
    directDepositDetail: {
      bankName: bankingInfo?.directDepositDetail?.bankName || '',
      holderName: bankingInfo?.directDepositDetail?.holderName || '',
      accountNumber: bankingInfo?.directDepositDetail?.accountNumber || '',
      accountNumberConfirmation:
        bankingInfo?.directDepositDetail?.accountNumber || '',
      routingNumber: bankingInfo?.directDepositDetail?.routingNumber || '',
      routingNumberConfirmation:
        bankingInfo?.directDepositDetail?.routingNumber || '',
      accountType: bankingInfo?.directDepositDetail?.accountType || null,
      ddEmailAddress: bankingInfo?.directDepositDetail?.ddEmailAddress || '',
      directDepositConfirmation:
        bankingInfo?.directDepositDetail?.isConfirmation || false,
    },
    mailMyChecksDetail: {
      payableTo: bankingInfo?.mailMyChecksDetail?.payableTo
        ? bankingInfo?.mailMyChecksDetail?.payableTo
        : entityInfo?.name || '',
      checkState: bankingInfo?.mailMyChecksDetail?.stateId
        ? bankingInfo?.mailMyChecksDetail?.stateId
        : undefined,
      checkCity: bankingInfo?.mailMyChecksDetail?.city || '',
      checkAddress: bankingInfo?.mailMyChecksDetail?.address || '',
      checkZip: bankingInfo?.mailMyChecksDetail?.zip || '',
      mailCheckConfirmation:
        bankingInfo?.mailMyChecksDetail?.isConfirmation || false,
    },
    internationalDetail: {
      accountHolderName:
        bankingInfo?.internationalDetail?.accountHolderName || '',
      internationalBankName:
        bankingInfo?.internationalDetail?.internationalBankName || '',
      internationalAccountNumber:
        bankingInfo?.internationalDetail?.internationalAccountNumber || '',
      swiftCode: bankingInfo?.internationalDetail?.swiftCode || '',
      internationalRoutingCode:
        bankingInfo?.internationalDetail?.internationalRoutingCode || '',
      internationalConfirmation1:
        bankingInfo?.internationalDetail?.confirmation1 || false,
      internationalConfirmation2:
        bankingInfo?.internationalDetail?.confirmation2 || false,
    },
    bankingFile: bankingFile?.fileKey ? [bankingFile?.fileKey] : [],
  };
  const handleModalCancel = () => {
    if (formRef.current?.isSubmitting) return;

    if (formRef.current?.dirty) {
      return Modal.confirm({
        title: 'Are you sure? The data will not be saved.',
        icon: <span className="icon icon-attention-filled" />,
        onOk() {
          onClose();
        },
        onCancel() {},
      });
    }
    onClose();
  };

  const handleSubmit = async (values: BankingInfoFormValues) => {
    const {
      bankingInfoType,
      directDepositDetail,
      mailMyChecksDetail,
      internationalDetail,
      bankingFile,
    } = values;

    const {
      bankName,
      holderName,
      accountNumber,
      routingNumber,
      accountType,
      ddEmailAddress,
      directDepositConfirmation,
    } = directDepositDetail;
    const {
      payableTo,
      checkState,
      checkCity,
      checkAddress,
      checkZip,
      mailCheckConfirmation,
    } = mailMyChecksDetail;
    const {
      accountHolderName,
      internationalBankName,
      internationalAccountNumber,
      swiftCode,
      internationalRoutingCode,
      internationalConfirmation1,
      internationalConfirmation2,
    } = internationalDetail;

    let data: BankingInformationRequestModel = {};

    if (isDirectDepositTypeSelected(bankingInfoType)) {
      data = {
        bankingInformationType: bankingInfoType,
        mailMyChecksDetail: undefined,
        internationalDetail: undefined,
        directDepositDetail: {
          bankName: bankName,
          holderName: holderName,
          accountNumber: accountNumber.trim(),
          routingNumber: routingNumber.trim(),
          accountType: accountType || undefined,
          ddEmailAddress: ddEmailAddress,
          isConfirmation: directDepositConfirmation,
        },
        bankInfoFileKey: bankingFile[0],
      };
    }

    if (isMailTheChecksTypeSelected(bankingInfoType)) {
      data = {
        bankingInformationType: bankingInfoType,
        directDepositDetail: undefined,
        internationalDetail: undefined,
        mailMyChecksDetail: {
          payableTo: payableTo,
          stateId: checkState ? checkState : undefined,
          city: checkCity,
          address: checkAddress,
          zip: checkZip || undefined,
          isConfirmation: mailCheckConfirmation,
        },
        bankInfoFileKey: bankingFile[0],
      };
    }

    if (isInternationalTypeSelected(bankingInfoType)) {
      data = {
        bankingInformationType: bankingInfoType,
        directDepositDetail: undefined,
        mailMyChecksDetail: undefined,
        internationalDetail: {
          accountHolderName: accountHolderName,
          internationalBankName: internationalBankName,
          internationalAccountNumber: internationalAccountNumber.trim(),
          swiftCode: swiftCode.trim(),
          internationalRoutingCode: internationalRoutingCode.trim(),
          confirmation1: internationalConfirmation1,
          confirmation2: internationalConfirmation2,
        },
        bankInfoFileKey: bankingFile[0],
      };
    }

    const correctAction = isAdmin
      ? saveRecruiterBankingInformation
      : saveBankingInformation;
    const requestData = {
      companyId: isAdmin ? companyId : undefined,
      bankingInfo: data,
    };

    const result = await dispatch(correctAction(requestData));

    if (correctAction.fulfilled.match(result)) {
      await onEdit();

      BaseAlert.success('Banking information saved.');
      onClose();
    } else {
      BaseAlert.error(result.error.message || ApiErrorMessage);
    }
  };

  return (
    <BaseModal
      title="Edit banking information"
      maxWidth={888}
      open={visible}
      onCancel={handleModalCancel}
    >
      <Formik
        enableReinitialize
        innerRef={formRef}
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {(formik) => (
          <Form layout="vertical">
            <div className="modal-scroll-wrapper">
              <Row gutter={48} align="middle">
                <Col xs={24} md={7}>
                  <div className="modal-title">Banking information</div>
                </Col>

                <Col xs={24} md={17} className="no-padding-md">
                  <FormItem name="bankingInfoType">
                    <Radio.Group
                      name="bankingInfoType"
                      className="radio-tab-view"
                    >
                      <Radio.Button
                        value={BOUsersBankingInfoTypeEnum.DirectDeposit}
                      >
                        Direct deposit
                      </Radio.Button>
                      <Radio.Button
                        value={BOUsersBankingInfoTypeEnum.MailMyChecks}
                      >
                        Mail the checks
                      </Radio.Button>
                      <Radio.Button
                        value={BOUsersBankingInfoTypeEnum.International}
                      >
                        International
                      </Radio.Button>
                    </Radio.Group>
                  </FormItem>
                </Col>
              </Row>

              {isDirectDepositTypeSelected(formik.values.bankingInfoType) && (
                <DirectDeposit />
              )}

              {isMailTheChecksTypeSelected(formik.values.bankingInfoType) && (
                <MailTheChecks formik={formik} />
              )}

              {isInternationalTypeSelected(formik.values.bankingInfoType) && (
                <International />
              )}

              <Divider className="modal-divider after-form" />

              <Row>
                <Col xs={24} md={12}>
                  <div className="upload-wrapper">
                    <BankingInformationUploader
                      defaultFile={bankingFile}
                      isAdmin={isAdmin}
                    />
                  </div>
                </Col>
              </Row>
            </div>

            <BaseModalFooter>
              <Button onClick={handleModalCancel}>Cancel</Button>

              <SubmitButton type="primary">Save</SubmitButton>
            </BaseModalFooter>
          </Form>
        )}
      </Formik>
    </BaseModal>
  );
}
